/**
 * NotificationBell - Header Notification Bell Icon
 * Animated bell with unread count badge
 */

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell } from 'lucide-react';
import { useNotificationStore, selectUnreadCount } from './notificationStore';

interface NotificationBellProps {
  onClick?: () => void;
  className?: string;
}

export const NotificationBell: React.FC<NotificationBellProps> = ({
  onClick,
  className = '',
}) => {
  const { togglePanel, isPanelOpen } = useNotificationStore();
  const unreadCount = useNotificationStore(selectUnreadCount);

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      togglePanel();
    }
  };

  // Animate when there are urgent notifications
  const shouldAnimate = unreadCount > 0;

  return (
    <motion.button
      onClick={handleClick}
      type="button"
      aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
      aria-expanded={isPanelOpen}
      title="Notifications"
      className={`relative p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors ${
        isPanelOpen ? 'bg-gray-100 text-gray-700' : ''
      } ${className}`}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
    >
      {/* Bell Icon */}
      <motion.div
        animate={
          shouldAnimate
            ? {
                rotate: [0, -10, 10, -10, 10, 0],
                transition: {
                  duration: 0.5,
                  repeat: Infinity,
                  repeatDelay: 5,
                },
              }
            : {}
        }
      >
        <Bell className="w-5 h-5" />
      </motion.div>

      {/* Unread Count Badge */}
      <AnimatePresence>
        {unreadCount > 0 && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center px-1"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Ping animation for new notifications */}
      {shouldAnimate && (
        <motion.span
          initial={{ opacity: 1, scale: 1 }}
          animate={{
            opacity: [1, 0],
            scale: [1, 1.5],
          }}
          transition={{
            duration: 1,
            repeat: Infinity,
            repeatDelay: 2,
          }}
          className="absolute top-0 right-0 w-4 h-4 bg-red-400 rounded-full"
        />
      )}
    </motion.button>
  );
};

export default NotificationBell;
